import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { JobCompletedData } from './types';

const emptyJob = {} as JobCompletedData;

/** Keep loaded details during background refreshes; only the latest request may publish. */
export function useCompletedJobRefresh(
  jobId: number | undefined,
  load: (id: number) => Promise<JobCompletedData>,
  onSuccess: (data: JobCompletedData) => void,
): [JobCompletedData, boolean, any, () => Promise<void>] {
  const activeJob = useRef({ jobId });
  if (activeJob.current.jobId !== jobId) activeJob.current = { jobId };
  const visit = activeJob.current;
  const mounted = useRef(true);
  const accept = useRef(onSuccess);
  accept.current = onSuccess;
  const sequence = useRef(0);
  const [snapshot, setSnapshot] = useState({ jobId, data: emptyJob, loading: true, error: null as any });
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; sequence.current += 1; };
  }, []);

  const refresh = useCallback(async () => {
    if (!jobId || !mounted.current || activeJob.current !== visit) return;
    const request = ++sequence.current;
    const isCurrent = () => mounted.current && activeJob.current === visit && sequence.current === request;
    setSnapshot(previous => ({ jobId, data: previous.jobId === jobId ? previous.data : emptyJob, loading: true, error: null }));
    try {
      const data = await load(jobId);
      if (!isCurrent()) return;
      setSnapshot({ jobId, data, loading: false, error: null });
      accept.current(data);
    } catch (error) {
      if (!isCurrent()) return;
      setSnapshot(previous => ({ ...previous, loading: false, error }));
    }
  }, [jobId, load, visit]);

  return snapshot.jobId === jobId
    ? [snapshot.data, snapshot.loading, snapshot.error, refresh]
    : [emptyJob, true, null, refresh];
}
